// RevenueOS shared UI helpers & format functions

export const GATE_LABELS = {
  customer_identified: "Customer Identified",
  technical_proposal: "Technical Proposal",
  customer_interest: "Customer Interest",
  pilot_commercial: "Pilot / Commercial",
  commercial_closure: "Commercial Closure",
};

export const GATES = [
  "customer_identified",
  "technical_proposal",
  "customer_interest",
  "pilot_commercial",
  "commercial_closure",
];

export const PMM_LEVELS = {
  explorer: { label: "Explorer", num: 1, color: "#6B6D75" },
  practitioner: { label: "Practitioner", num: 2, color: "#3B82F6" },
  specialist: { label: "Specialist", num: 3, color: "#10B981" },
  growth_partner: { label: "Growth Partner", num: 4, color: "#F59E0B" },
  strategic_partner: { label: "Strategic Partner", num: 5, color: "#E56B42" },
};

// Score → PMM level bands (mirrors backend PMM_LEVEL_BANDS in server.py).
// A partner's level is derived from their overall PMM score by default.
export const PMM_LEVEL_BANDS = [
  { min: 85, key: "strategic_partner" },
  { min: 75, key: "growth_partner" },
  { min: 65, key: "specialist" },
  { min: 55, key: "practitioner" },
  { min: 0, key: "explorer" },
];

export function pmmLevelForScore(score) {
  const band = PMM_LEVEL_BANDS.find(b => (score || 0) >= b.min);
  return band ? band.key : "explorer";
}

export const PMM_DIMENSIONS = [
  { key: "product_knowledge", label: "Product Knowledge" },
  { key: "technical_capability", label: "Technical" },
  { key: "sales_capability", label: "Sales" },
  { key: "customer_success", label: "Customer Success" },
  { key: "delivery_excellence", label: "Delivery" },
  { key: "ai_adoption", label: "AI Adoption" },
  { key: "collaboration", label: "Collaboration" },
  { key: "revenue_growth", label: "Revenue" },
  { key: "learning_certification", label: "Learning" },
  { key: "community_contribution", label: "Community" },
];

export const HEALTH_COLORS = {
  at_risk: "#991B1B",
  watch: "#F59E0B",
  healthy: "#22C55E",
  excellent: "#166534",
};

export const TEMPERATURE_COLORS = {
  cold: "#3B82F6",
  warm: "#F59E0B",
  hot: "#E56B42",
  burning: "#EF4444",
};

/** D-02: all monetary values stored and displayed in ₹ Lakh (`*_cr` suffix is legacy). */
export const MONEY_UNIT = "Lakh";
export const MONEY_UNIT_LABEL = "₹ Lakh";
export const MONEY_UNIT_SHORT = "₹ L";

/**
 * Format monetary values (stored in lakhs). Uses Indian grouping for large amounts.
 */
export function formatLakh(v) {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  const s = Math.abs(n) >= 1000
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : String(n).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return `₹${s} L`;
}

/** @deprecated Use formatLakh — kept so existing imports keep working. */
export function formatCr(v) {
  return formatLakh(v);
}

/** @deprecated Use formatLakh — Universe company revenue is also stored in lakhs (D-02). */
export function formatCompanyRevenueCr(v) {
  return formatLakh(v);
}

/** Parse a user-entered lakh amount without scaling. */
export function parseLakhInput(v) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return formatDate(iso);
}

export function initials(name = "") {
  return name.split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
}

/** User-facing meeting type labels (B-11: legacy `whatsapp` stored value → Ask Niq). */
export const MEETING_TYPE_LABELS = {
  teams: "Teams",
  zoom: "Zoom",
  meet: "Google Meet",
  phone: "Phone",
  physical: "In person",
  site_visit: "Site visit",
  demo: "Demo",
  pilot_review: "Pilot review",
  ask_niq: "Ask Niq",
  whatsapp: "Ask Niq",
};

export const MEETING_TYPE_OPTIONS = [
  "teams", "zoom", "meet", "phone", "physical", "site_visit", "demo", "pilot_review", "ask_niq",
];

export function formatMeetingType(type) {
  if (!type) return "Meeting";
  return MEETING_TYPE_LABELS[type] || String(type).replace(/_/g, " ");
}

/** User-facing signal source labels (B-11). */
export const SIGNAL_SOURCE_LABELS = {
  manual: "Manual",
  linkedin: "LinkedIn",
  website: "Website",
  news: "News",
  tender: "Tender",
  referral: "Referral",
  email: "Email",
  ask_niq: "Ask Niq",
  whatsapp: "Ask Niq",
};

export function formatSignalSource(source) {
  if (!source) return "";
  const key = String(source).toLowerCase();
  return SIGNAL_SOURCE_LABELS[key] || String(source).replace(/_/g, " ");
}

/** B-12: bottom border always reserved so focus does not shift layout */
export const STABLE_INPUT_CLASS =
  "w-full bg-transparent border-b border-black/10 focus:border-[var(--copper)] outline-none transition-colors";
